<template>
  <canvas
    data-cover-player-canvas
    :width="canvasSize.width"
    :height="canvasSize.height"
  />
</template>
<script>
import detectLines from '../../utils/detectLines';

const loadedFonts = {};
const loadedImages = {};

export default {
  props: ['screenHeight', 'screenRatio', 'coverParams', 'fontNameLookup', 'hasStamp', 'stampRule', 'filterId'],
  computed: {
    canvasSize() {
      const height = Math.round(this.screenHeight);
      const width = Math.round(this.screenRatio * this.screenHeight);

      return { width, height };
    },
    usedStampUrl() {
      if (this.hasStamp) {
        return this.stampRule.img.file;
      }
      return '';
    },
    stampMarker() {
      return this.hasStamp ? JSON.stringify(this.stampRule) : '';
    }
  },
  methods: {
    draw() {
      if (this.drawing) {
        this.drawNext = true;
      } else {
        /**
         * @type{CanvasRenderingContext2D}
         */
        const context = this.$el.getContext('2d');
        context.clearRect(0, 0, this.$el.width, this.$el.height);

        this.drawing = true;

        [].concat(this.coverParams).reduce(async (prev, item) => {
          await prev;
          context.save();
          await this.drawCoverItem(item, context);
          context.restore();
        }, Promise.resolve()).then(async () => {
          if (this.hasStamp) {
            const img = await this.getStampImage();
            const { type, size, alpha, position } = this.stampRule.img;
            context.save();
            if (type) {
              context.filter = `url(#filter${this.filterId})`;
            }
            context.globalAlpha = alpha / 100;
            context.drawImage(img, position[0], position[1], size[0], size[1]);
            context.restore();
          }
          requestAnimationFrame(() => {
            this.drawing = false;
            if (this.drawNext) {
              this.drawNext = false;
              this.draw();
            }
          });
        });
      }
    },

    async drawCoverItem(item, context) {
      // rotation
      context.translate(item.left + item.width / 2, item.top + item.height / 2);
      context.rotate(item.rotate * Math.PI / 180);
      context.translate(-item.left - item.width / 2, -item.top - item.height / 2);

      if (item.opacity < 100 || item.letterSpacing > 0) {
        const otherCanvas = document.createElement('canvas');
        otherCanvas.width = item.width;
        otherCanvas.height = item.height;
        if (item.letterSpacing > 0) {
          otherCanvas.className = 'effect-cover__measure';
          otherCanvas.style.letterSpacing = item.letterSpacing * item.fontSize / 100 + 'px';
          document.body.appendChild(otherCanvas);
        }
        await this.drowOpaqueCoverItem({
          ...item,
          top: 0,
          left: 0
        }, otherCanvas.getContext('2d'));

        context.globalAlpha = item.opacity / 100;
        context.drawImage(otherCanvas, item.left, item.top);
        if (item.letterSpacing > 0) {
          document.body.removeChild(otherCanvas);
        }
      } else {
        await this.drowOpaqueCoverItem(item, context);
      }
    },
    /**
     * @param {{
     * text: string,
     * fontFamily: string,
     * fontSize: number,
     * lineHeight: number,
     * autoFontSize: boolean,
     * width: number,
     * height: number,
     * top: number,
     * left: number,
     * color: string,
     * stroke: string,
     * strokeWidth: number,
     * rotate: number,
     * align: string,
     * verticalAlign: string,
     * opacity: number,
     * useShadow: boolean,
     * shadow: {},
     * background: string,
     * type?: string
     * }} item
     * @param {CanvasRenderingContext2D} context
     */
    async drowOpaqueCoverItem(item, context) {
      if (item.type) {
        return this.drawStickerCoverItem(item, context);
      }
      const getGradient = (gd) => {
        const gradient = gd.type === 'linear'
          ? context.createLinearGradient(
            item.left + gd.x1 * item.width,
            item.top + gd.y1 * item.height,
            item.left + gd.x2 * item.width,
            item.top + gd.y2 * item.height
          ) : context.createRadialGradient(
            item.left + .5 * item.width,
            item.top + .5 * item.height,
            0,
            item.left + .5 * item.width,
            item.top + .5 * item.height,
            Math.sqrt(Math.pow(item.width, 2) + Math.pow(item.height, 2)) / 2
          );

        gd.stops.forEach(s => {
          gradient.addColorStop(s.offset / 100, s.color);
        });
        return gradient;
      };

      // background first
      if (item.background) {
        context.fillStyle = item.background;
        context.fillRect(item.left, item.top, item.width, item.height);
      }
      if (item.gradient) {
        context.fillStyle = getGradient(item.gradient);
      } else {
        context.fillStyle = item.color;
      }
      context.font = `${item.fontSize}px/${item.lineHeight} ${item.fontFamily}`;
      if (item.strokeGradient) {
        context.strokeStyle = getGradient(item.strokeGradient);
      } else {
        context.strokeStyle = item.stroke;
      }
      context.lineWidth = item.strokeWidth;
      context.textAlign = item.align;

      if (item.useShadow && item.shadow) {
        context.shadowOffsetX = item.shadow.x;
        context.shadowOffsetY = item.shadow.y;
        context.shadowBlur = item.shadow.blur;
        context.shadowColor = 'transparent';
      }

      let x = item.left;
      let y = item.top;
      switch (item.align) {
        case 'right':
          x += item.width;
          break;
        case 'center':
          x += item.width / 2;
          break;
      }
      const arr = await detectLines(item, 1);

      switch (item.verticalAlign) {
        case 'middle':
          context.textBaseline = 'middle';
          y += item.height / 2 - (arr.length - 1) * item.lineHeight * item.fontSize / 2;
          break;
        case 'bottom':
          context.textBaseline = 'bottom';
          y += item.height - ((arr.length - .5) * item.lineHeight - .5) * item.fontSize;
          break;
        default:
          context.textBaseline = 'top';
          y += (item.lineHeight - 1) / 2 * item.fontSize;
          break;
      }

      arr.forEach((text, i) => {
        if (item.strokeWidth) {
          context.strokeText(text, x, y + i * item.lineHeight * item.fontSize);
        }
        if (item.shadow) {
          context.shadowColor = item.shadow.color;
        }
        context.fillText(text, x, y + i * item.lineHeight * item.fontSize);
        context.shadowColor = 'transparent';
      });
    },
    detectFonts() {
      const obj = {};

      this.coverParams.forEach(({ fontFamily }) => {
        if (fontFamily in this.fontNameLookup && !(fontFamily in loadedFonts)) {
          obj[fontFamily] = 1;
        }
      });

      // TODO:
      /**
       * @type{HTMLCanvasElement}
       */
      const canvas = this.$el;
      const context = canvas.getContext('2d');
      const detected = Object.keys(obj).every(font => {
        if (font.toLowerCase() === 'courier new') {
          return true;
        }

        context.font = `30px ${font},'Courier New'`;
        const { width } = context.measureText('anything');
        context.font = `30px 'Courier New'`;
        const { width: otherWidth } = context.measureText('anything');

        return width !== otherWidth;
      });

      if (detected) {
        this.draw();
      } else {
        setTimeout(this.detectFonts, 1e3);
      }
    },
    /**
     * @param{CanvasRenderingContext2D} context
     */
    async drawStickerCoverItem(item, context) {
      const img = await this.getStickerImage(item.stickerHTML);

      if (item.useShadow && item.shadow) {
        context.shadowOffsetX = item.shadow.x;
        context.shadowOffsetY = item.shadow.y;
        context.shadowBlur = item.shadow.blur;
        context.shadowColor = item.shadow.color;
      }
      context.drawImage(img, item.left, item.top, item.width, item.height);
    },
    /**
     * @returns{Promise<HTMLImageElement>}
     */
    getStickerImage(source) {
      if (source in loadedImages) {
        return loadedImages[source];
      }

      const blob = new Blob([source], { type: 'image/svg+xml' });
      const url = URL.createObjectURL(blob);
      const ret = new Promise((resolve, reject) => {
        const img = document.createElement('img');
        img.onload = () => {
          document.body.removeChild(img);
          URL.revokeObjectURL(url);
          resolve(img);
        };
        img.onerror = reject;
        img.src = url;
        document.body.appendChild(img);
      });
      loadedImages[source] = ret;
      return ret;
    },
    getStampImage() {
      const url = this.usedStampUrl;
      if (url in loadedImages) {
        return loadedImages[url];
      }

      const ret = new Promise((resolve, reject) => {
        const img = document.createElement('img');
        img.crossOrigin = true;
        img.onload = () => {
          document.body.removeChild(img);
          resolve(img);
        };
        img.onerror = reject;
        img.src = url;
        document.body.appendChild(img);
      });
      loadedImages[url] = ret;
      return ret;
    }
  },
  mounted() {
    this.draw();
    setTimeout(this.detectFonts, 50);
  },
  watch: {
    coverParams: {
      handler() {
        this.draw();
        setTimeout(this.detectFonts, 50);
      },
      deep: true
    },
    canvasSize() {
      this.draw();
    },
    fontNameLookup() {
      this.detectFonts();
    },
    stampMarker() {
      this.draw();
    },
    filterId(val) {
      if (val && this.hasStamp) {
        this.$nextTick(this.draw);
      }
    }
  }
};
</script>